import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdInsertLink, MdOutlineSearch } from 'react-icons/md';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { useUsersSearch } from 'legacy/search/hooks';
import { useDebounce } from 'use-debounce';
import EmptyItemSelectionList from '../EmptyItemSelectionList';
import ItemList, { Item } from '../ItemList';
import { AvatarRadii } from 'components/Avatar';
import { PromoterOfferContractDto } from 'legacy/events/types';

const UserSelectionModal = ({
  users,
  contracts,
  onClose,
  onSubmit
}: {
  users: Item[];
  contracts: PromoterOfferContractDto[];
  onClose: () => void;
  onSubmit: (users: Item[]) => void;
}) => {
  const { t } = useTranslation(['events']);
  const [selectedUsers, setSelectedUsers] = useState<Item[]>(users);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 200);

  const { data: searchedUsers, isLoading } = useUsersSearch(debouncedSearch);
  const claimedUsers = contracts.flatMap((contract) => contract.promoter.id);

  const handleSubmit = () => {
    if (search.length > 0) {
      setSearch('');
    } else {
      onSubmit(selectedUsers);
    }
  };

  const eligibleUsersCount =
    selectedUsers.length > 0 ? `• ${selectedUsers.length}` : '';
  const footerText =
    search.length > 0
      ? t('settings.promoterOffers.userSelection.saveSelection')
      : `${t('settings.promoterOffers.userSelection.addEligibleUsers')} ${eligibleUsersCount}`;

  const handleSearch = ({ currentTarget }) => {
    setSearch(currentTarget.value);
  };

  const showEmptyState =
    !isLoading &&
    !debouncedSearch &&
    !searchedUsers?.length &&
    !selectedUsers?.length;

  const handleSelectUser = (item: Item) => {
    const newSelectedUsers = selectedUsers.some((u) => u.id === item.id)
      ? selectedUsers.filter((u) => u.id !== item.id)
      : [...selectedUsers, item];
    setSelectedUsers(newSelectedUsers);
  };

  const handleClearAll = () => {
    setSelectedUsers([]);
  };

  const usersToDisplay =
    search.length > 0
      ? searchedUsers?.map((user) => ({
          id: user._id,
          name: user.name,
          photoUrl: user?.photo?.url || ''
        }))
      : selectedUsers;

  return (
    <Modal
      visible
      autoHeight
      type={ModalType.SMALL}
      onClose={onClose}
      shouldStopModalClose={false}
      title={t('settings.promoterOffers.userSelection.selectBounceUsers')}
      icon={<MdInsertLink size={20} />}
      renderFooter={() => (
        <Button
          type={ButtonType.Primary}
          onClick={handleSubmit}
          loading={false}
        >
          {footerText}
        </Button>
      )}
    >
      <div className="px-4 pt-4 flex flex-col justify-between">
        <p className="mt-4 text-base font-normal">
          {t('settings.promoterOffers.userSelection.description')}
        </p>
        <div className="relative shrink-0 flex h-12 border-1 border-gray-300 rounded-full mt-6">
          <MdOutlineSearch
            className="absolute left-3 text-gray-400 self-center justify-center"
            size={24}
          />
          <input
            type="text"
            className="w-full h-full px-11 rounded-full text-base text-gray-600 placeholder:text-gray-400"
            placeholder={t('settings.promoterOffers.userSelection.searchPlaceholder')}
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div>
          {showEmptyState ? (
            <EmptyItemSelectionList message={t('settings.promoterOffers.selectionModals.users.emptyState')} />
          ) : (
            <div className="overflow-y-auto mt-4">
              <ItemList
                isLoading={isLoading}
                items={usersToDisplay}
                onSelect={handleSelectUser}
                selectedItems={selectedUsers}
                search={search}
                onClearAll={handleClearAll}
                clearButtonText={`${selectedUsers.length} Bounce Users`}
                avatarRadius={AvatarRadii.Full}
                claimedUsers={claimedUsers}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserSelectionModal;
